/**
 * IncassoDebtChart
 *
 * Debt-over-time line chart for incasso dossiers.
 * Shows total outstanding debt and active case count from daily snapshots.
 * Pattern follows HelpdeskMetricsBlockNew (Chart.js + react-chartjs-2).
 */

import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useSession } from '@clerk/clerk-react';
import { ChartLineUp } from '@phosphor-icons/react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
);

interface SnapshotDataPoint {
  period: string;
  periodLabel: string;
  totalDebt: number;
  activeCases: number;
  avgDaysOverdue: number;
}

interface SnapshotTimeSeries {
  granularity: string;
  data: SnapshotDataPoint[];
}

type Granularity = 'day' | 'week' | 'month';

const formatEuro = (amount: number): string =>
  new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

export const IncassoDebtChart = () => {
  const { session } = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const chartRef = useRef<any>(null);

  const [granularity, setGranularity] = useState<Granularity>('day');
  const [timeSeries, setTimeSeries] = useState<SnapshotTimeSeries | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTimeSeries = useCallback(async () => {
    if (!sessionRef.current) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ granularity });
      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-tickets/snapshots/time-series?${params}`,
        { method: 'GET' },
        sessionRef.current,
      );
      if (!response.ok) throw new Error('Failed to fetch snapshot time series');
      const data: SnapshotTimeSeries = await response.json();
      setTimeSeries(data);
    } catch (error) {
      console.error('[IncassoDebtChart] Error fetching time series:', error);
    } finally {
      setIsLoading(false);
    }
  }, [granularity]);

  useEffect(() => {
    fetchTimeSeries();
  }, [fetchTimeSeries]);

  const chartData = useMemo(() => {
    if (!timeSeries?.data || timeSeries.data.length === 0) return null;

    return {
      labels: timeSeries.data.map((d) => d.periodLabel),
      datasets: [
        {
          label: 'Openstaande klantenfacturen minstens 15 dagen vervallen',
          data: timeSeries.data.map((d) => d.totalDebt),
          borderColor: '#EA8162',
          backgroundColor: 'rgba(234, 129, 98, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 6,
          pointBackgroundColor: '#EA8162',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          borderWidth: 2,
          yAxisID: 'y',
        },
        {
          label: 'Actieve dossiers',
          data: timeSeries.data.map((d) => d.activeCases),
          borderColor: '#5B7304',
          backgroundColor: 'rgba(91, 115, 4, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 6,
          pointBackgroundColor: '#5B7304',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          borderWidth: 2,
          yAxisID: 'y1',
        },
      ],
    };
  }, [timeSeries]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            usePointStyle: true,
            padding: 12,
            boxWidth: 6,
            font: { size: 11 },
          },
        },
        tooltip: {
          backgroundColor: '#31403B',
          titleColor: '#F9F0EE',
          bodyColor: '#F9F0EE',
          borderColor: '#EA8162',
          borderWidth: 1,
          padding: 10,
          titleFont: { size: 12 },
          bodyFont: { size: 11 },
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              if (context.datasetIndex === 0) {
                return `${label}: ${formatEuro(value)}`;
              }
              return `${label}: ${value}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { size: 10 },
            maxRotation: 0,
          },
        },
        y: {
          type: 'linear' as const,
          display: true,
          position: 'left' as const,
          beginAtZero: true,
          grid: { color: 'rgba(49, 64, 59, 0.1)' },
          ticks: {
            font: { size: 10 },
            callback: (value: any) => formatEuro(value),
          },
        },
        y1: {
          type: 'linear' as const,
          display: true,
          position: 'right' as const,
          beginAtZero: true,
          grid: { drawOnChartArea: false },
          ticks: {
            font: { size: 10 },
            stepSize: 10,
          },
        },
      },
    }),
    [],
  );

  // Latest values for summary
  const latestPoint = timeSeries?.data?.[timeSeries.data.length - 1];
  const previousPoint = timeSeries?.data?.[timeSeries.data.length - 2];
  const debtDelta = latestPoint && previousPoint
    ? latestPoint.totalDebt - previousPoint.totalDebt
    : null;

  if (!timeSeries?.data || timeSeries.data.length === 0) {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center rounded-lg border border-neutral-200 bg-white p-6">
          <span className="text-sm text-neutral-secondary-mid">Grafiek laden...</span>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center rounded-lg border border-neutral-200 bg-white p-6">
        <span className="text-sm text-neutral-secondary-mid">
          Nog geen snapshot data beschikbaar. Klik op "Vernieuwen" om de eerste snapshot te maken.
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-neutral-secondary-mid">
          <ChartLineUp weight="duotone" size={16} />
          <span className="font-medium">Schuld Evolutie</span>
        </div>

        <div className="flex items-center gap-3">
          {/* Summary stats */}
          {latestPoint && (
            <div className="flex items-center gap-4 text-xs">
              <span>
                <span className="font-semibold text-[#EA8162]">
                  {formatEuro(latestPoint.totalDebt)}
                </span>{' '}
                huidig
              </span>
              <span>
                <span className="font-semibold text-[#5B7304]">
                  {latestPoint.activeCases}
                </span>{' '}
                dossiers
              </span>
              {debtDelta !== null && (
                <span
                  className={`font-semibold ${
                    debtDelta > 0
                      ? 'text-red-600'
                      : debtDelta < 0
                        ? 'text-green-600'
                        : 'text-neutral-500'
                  }`}
                >
                  {debtDelta > 0 ? '+' : ''}
                  {formatEuro(debtDelta)}
                </span>
              )}
            </div>
          )}

          {/* Granularity selector */}
          <Select
            value={granularity}
            onValueChange={(value) => setGranularity(value as Granularity)}
          >
            <SelectTrigger className="h-7 w-[100px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              position="popper"
              sideOffset={4}
              align="end"
              className="max-h-[200px]"
            >
              <SelectItem value="day">Per dag</SelectItem>
              <SelectItem value="week">Per week</SelectItem>
              <SelectItem value="month">Per maand</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Chart */}
      {chartData && (
        <div className="h-[200px] w-full">
          <Line ref={chartRef} data={chartData} options={chartOptions} />
        </div>
      )}
    </div>
  );
};
